import { useState, useRef, useCallback, useEffect } from "react";
import api from "../api/axios";

/**
 * Customer visual search: POST multipart to /ProductRoutes/customer/visual-search.
 * @param {React.RefObject} toastRef - PrimeReact Toast ref for messages
 * @param {{ onResults?: (products: object[]) => void }} [options] - If set, results are not stored in hook state (e.g. Home → navigate to Shop)
 */
export function useVisualImageSearch(toastRef, options = {}) {
  const { onResults } = options;
  const fileInputRef = useRef(null);
  const [visualLoading, setVisualLoading] = useState(false);
  const [visualResults, setVisualResults] = useState(null);
  const [visualPreviewUrl, setVisualPreviewUrl] = useState(null);
  const [visualCardLoaded, setVisualCardLoaded] = useState({});

  useEffect(() => {
    return () => {
      if (visualPreviewUrl) URL.revokeObjectURL(visualPreviewUrl);
    };
  }, [visualPreviewUrl]);

  const runVisualSearch = useCallback(
    async (file) => {
      if (!file || !file.type.startsWith("image/")) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Invalid file",
          detail: "Please upload an image file",
          life: 3000,
        });
        return;
      }
      const url = URL.createObjectURL(file);
      setVisualPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      if (!onResults) {
        setVisualResults(null);
      }
      setVisualLoading(true);
      setVisualCardLoaded({});
      try {
        const fd = new FormData();
        fd.append("image", file);
        const res = await api.post("/ProductRoutes/customer/visual-search", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.success) {
          const data = res.data.data || [];
          if (onResults) {
            onResults(data);
          } else {
            setVisualResults(data);
          }
          if (!data.length) {
            toastRef.current?.show({
              severity: "info",
              summary: "No similar images found",
              detail: "Try a different image",
              life: 3000,
            });
          }
        }
      } catch {
        toastRef.current?.show({
          severity: "error",
          summary: "Search failed",
          detail: "Could not complete image search",
          life: 3000,
        });
        if (!onResults) {
          setVisualResults([]);
        }
      } finally {
        setVisualLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [onResults, toastRef]
  );

  const clearVisualSearch = useCallback(() => {
    setVisualResults(null);
    setVisualPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setVisualCardLoaded({});
  }, []);

  return {
    fileInputRef,
    visualLoading,
    visualResults,
    visualPreviewUrl,
    visualCardLoaded,
    setVisualCardLoaded,
    runVisualSearch,
    clearVisualSearch,
    setVisualResults,
    setVisualLoading,
  };
}
